// built-from: /drafts-tray v1 impl:drafts-tray role:implementation
// The drafts tray (054-facilitated-vision, T044 — FR-014/FR-022, US4;
// wireframe §2.3, §3 row 9, §4). The ONLY home of `uncertain` facts: the gate
// is applied by the SHELL — this tray renders exactly what arrives in
// SurfaceFactState.drafts. Collapsed by default and pinned at the surface
// foot; each entry shows its draft text, a `Draft · <source>` text+icon tag
// (never colour alone), an explicit "Add to board" raise (uncertain →
// assumed) and a Reject. Every mutation flows through onEmitEdit — the tray
// never fetches or writes.
//
// T030 review pickup pinned here: the raise control DISABLES after fire so a
// double-click can never reach the server twice (vision.raise_draft refuses a
// non-uncertain fact with a 400 — the surface makes that unreachable).

import React, { useState } from 'react';
import { ArrowUp, ChevronDown, ChevronRight, FileText, Mic, Trash2 } from 'lucide-react';
import type { GatedFact, SurfaceEdit } from '../../facilitation/surfaceBinding';
import { InPlaceField } from './factCardKit';
import '../../styles/DraftsTray.css';

export interface DraftsTrayProps {
  /** SurfaceFactState.drafts — the shell-gated uncertain facts. */
  drafts: GatedFact[];
  /** Sealed session (§3 row 12/§4): the tray stays readable, its raise and
   * reject affordances are REMOVED (the fold toggle is a non-mutating view
   * toggle and survives). */
  sealed?: boolean;
  /** Close-in-flight freeze: affordances render but disabled. */
  busy?: boolean;
  onEmitEdit: (edit: SurfaceEdit) => void | boolean | Promise<boolean | void>;
}

// The provenance-derived source label (§2.3): a document draft names its
// document; a voice draft that could not be attributed reads plainly.
function sourceOf(draft: GatedFact): { label: string; icon: React.ReactNode } {
  const statedBy = draft.provenance?.statedBy as { kind?: string; ref?: string } | undefined;
  const modality = draft.provenance?.modality;
  const source = draft.provenance?.source as {
    sourceRef?: string;
    documents?: Array<{ filename?: string }>;
  } | undefined;
  if (modality === 'attachment') {
    const filenames = source?.documents?.map((document) => document.filename).filter(Boolean);
    return {
      label: filenames?.join(', ') || source?.sourceRef || statedBy?.ref || 'attached document',
      icon: <FileText size={12} aria-hidden="true" />,
    };
  }
  if (statedBy?.kind === 'document') {
    return {
      label: statedBy.ref || 'attached document',
      icon: <FileText size={12} aria-hidden="true" />,
    };
  }
  if (modality === 'voice') {
    return { label: source?.sourceRef || 'voice contribution', icon: <Mic size={12} aria-hidden="true" /> };
  }
  return { label: 'conversation', icon: <Mic size={12} aria-hidden="true" /> };
}

const DraftsTray: React.FC<DraftsTrayProps> = ({ drafts, sealed, busy, onEmitEdit }) => {
  const [expanded, setExpanded] = useState(false);
  // Raise controls that have fired (per factId) — disabled until the state
  // the shell delivers moves the fact out of the tray (or the reject reverts).
  const [raised, setRaised] = useState<Record<string, boolean>>({});

  const raise = (factId: string) => {
    if (raised[factId]) return; // belt over the disabled attribute
    setRaised((prev) => ({ ...prev, [factId]: true }));
    onEmitEdit({ capabilityId: 'vision.raise_draft', input: { factId } });
  };

  return (
    <section className="dtray">
      <button
        type="button"
        className="dtray-toggle"
        aria-expanded={expanded}
        onClick={() => setExpanded((open) => !open)}
      >
        {expanded ? (
          <ChevronDown size={14} aria-hidden="true" />
        ) : (
          <ChevronRight size={14} aria-hidden="true" />
        )}{' '}
        Drafts ({drafts.length}) · not on the board yet
      </button>

      {expanded && drafts.length > 0 && (
        <ul className="dtray-list">
          {drafts.map((draft) => {
            const source = sourceOf(draft);
            return (
              <li key={draft.factId} data-fact-id={draft.factId} className="dtray-entry">
                <span className="dtray-tag">
                  {source.icon} Draft · {source.label}
                </span>
                <InPlaceField
                  fact={draft}
                  noun="draft"
                  disabled={Boolean(sealed || busy || draft.provisionalLocal)}
                  onEmitEdit={onEmitEdit}
                />
                {!sealed && (
                  <div className="dtray-actions">
                    <button
                      type="button"
                      className="dtray-raise"
                      disabled={!!raised[draft.factId] || draft.provisionalLocal || busy}
                      onClick={() => raise(draft.factId)}
                    >
                      <ArrowUp size={13} aria-hidden="true" /> Add to board
                    </button>
                    <button
                      type="button"
                      className="dtray-reject"
                      aria-label="Reject draft"
                      disabled={draft.provisionalLocal || busy}
                      onClick={() =>
                        onEmitEdit({
                          capabilityId: 'vision.reject_fact',
                          input: { factId: draft.factId },
                        })
                      }
                    >
                      <Trash2 size={13} aria-hidden="true" />
                    </button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
};

export default DraftsTray;
